import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@carrier-io/air-react';
import Stack from '@carrier-io/air-react/Stack';
import { StatBox } from 'components/StatBox';
import SearchBox from '@carrier-io/air-react/patterns/SearchBox';
import Button from '@carrier-io/air-react/Button';
import Tab from '@carrier-io/air-react/Tab';
import TabContext from '@carrier-io/air-react/TabContext';
import TabPanel from '@carrier-io/air-react/TabPanel';
import TabList from '@carrier-io/air-react/TabList';
import Box from '@carrier-io/air-react/Box';
import ButtonBase from '@carrier-io/air-react/ButtonBase';
import { BaseLayout } from 'layouts/BaseLayout';
import { ProjectPopup } from 'components/popups/ProjectPopup';
import { UserPopup } from 'components/popups/UserPopup';
import { VendorLogo } from 'components/VendorLogo';
import EditIcon from 'components/icons/EditIcon';
import DeleteIcon from 'components/icons/DeleteIcon';
import { VendorPopup } from 'components/popups/VendorPopup';
import { Loader } from 'components/Loader';
import { useNotification } from 'context/NotificationContext';
import { NotificationBgColor } from 'store/models/Notification';
import { ConfirmPopup } from 'components/popups/ConfirmPopup';
import { Vendor, VendorRepresentative, VendorRoles } from 'store/models/models';
import { DataProducerProject, isProjectExpired, isProjectOnboarding } from 'store/models/Project';
import { VendorStats } from 'store/models/Stats';
import { useHandleError } from 'hooks/useHandleError';
import { PrivateRoute } from 'constants/routes';
import { Variant } from 'constants/componentEnums';
import { MapStatusToComponent } from 'utils/mappers';
import { calcPublishedMessages } from 'utils/calcPublishedMessages';
import { isIdValid } from 'utils/validators';
import { getTLProjects } from 'utils/getTLProjects';
import { getVendorAddress } from 'utils/getVendorAddress';
import { useAuth } from 'context/AuthContext';
import { DeleteUserData, DeleteUserInput, UserRoles } from 'store/models/User';
import { GET_VENDOR_BY_ID } from '../../store/graphql/queries/GET_VENDOR_BY_ID';
import { GET_COUNT_OF_REGISTERED_DEVICES } from 'store/graphql/queries/GET_COUNT_OF_REGISTERED_DEVICES';
import { GET_COUNT_OF_PUBLISHED_MESSAGES } from 'store/graphql/queries/GET_COUNT_OF_PUBLISHED_MESSAGES';
import { GET_ERROR_MESSAGES_FOR_PROJECT } from 'store/graphql/queries/GET_ERROR_MESSAGES_FOR_PROJECT';
import { DELETE_VENDOR_REPRESENTATIVE } from 'store/graphql/mutations/DELETE_VENDOR_REPRESENTATIVE';
import styles from '../../styles/Dashboard.module.css';

const emptyVendor: Vendor = {
  projects: [],
  vendorName: '',
  vendorId: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  contactInfo: {
    city: '',
    country: '',
    state: '',
    street: '',
    zip: 0,
  },
  paperWorkFinished: false,
  vendorRepresantatives: [],
};

const enum PageTabs {
  Project = 0,
  User = 1,
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const MemberVendorProjects = () => {
  const navigate = useNavigate();
  const { vendorId } = useParams();
  const { currentUserRoles } = useAuth();
  const isAdmin = useMemo(() => currentUserRoles?.includes(UserRoles.ADMIN), [currentUserRoles]);
  const { setIsNotificationOpened, setTextContent, setNotificationBgColor } = useNotification();

  const [vendorData, setVendorData] = useState<Vendor>(emptyVendor);

  const [rawProjectsList, setRawProjectsList] = useState<DataProducerProject[]>([]);
  const [projectsSearch, setProjectsSearch] = useState<string>('');

  const [rawUsersList, setRawUsersList] = useState<VendorRepresentative[]>([]);
  const [userSearch, setUserSearch] = useState<string>('');

  const [isEditingProject, setIsEditingProject] = useState(false);
  const [isEditingVendor, setIsEditingVendor] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editableUser, setEditableUser] = useState<VendorRepresentative | null>(null);
  const [editableProject, setEditableProject] = useState<DataProducerProject | null>(null);

  const [vendorPopupOpened, setVendorPopupOpened] = useState(false);

  const [pagination, setPagination] = useState({
    currPage: 0,
    itemsPerPage: 5,
    currUserPage: 0,
    itemsUserPerPage: 5,
  });

  const [activeTab, setActiveTab] = useState(PageTabs.Project.toString());
  const [projectPopupOpened, setProjectPopupOpened] = useState(false);
  const [usersPopupOpened, setUsersPopupOpened] = useState(false);
  const [confirmPopupOpened, setConfirmPopupOpened] = useState(false);
  const [warningPopupOpened, setWarningPopupOpened] = useState(false);
  const [warningText, setWarningText] = useState('');
  const [deleteUserData, setDeleteUserData] = useState<DeleteUserData | null>(null);
  const [vendorStats, setVendorStats] = useState<VendorStats | null>(null);
  const searchedProjects = useMemo(() => {
    return projectsSearch.length > 0
      ? rawProjectsList.filter((project) => project.name.includes(projectsSearch))
      : rawProjectsList;
  }, [projectsSearch, rawProjectsList]);

  const searchedUsers = useMemo(() => {
    return userSearch.length > 0
      ? rawUsersList.filter(
          (user) =>
            user.firstName.toLowerCase().includes(userSearch.toLowerCase()) ||
            user.lastName.toLowerCase().includes(userSearch.toLowerCase()),
        )
      : rawUsersList;
  }, [userSearch, rawUsersList]);

  const paginatedList = useMemo(() => {
    const { currPage, itemsPerPage } = pagination;
    return searchedProjects?.slice(currPage * itemsPerPage, (currPage + 1) * itemsPerPage);
  }, [searchedProjects, pagination.currPage, pagination.itemsPerPage]);

  const paginatedUserList = useMemo(() => {
    const { currUserPage, itemsUserPerPage } = pagination;
    return searchedUsers?.slice(currUserPage * itemsUserPerPage, (currUserPage + 1) * itemsUserPerPage);
  }, [searchedUsers, pagination.currUserPage, pagination.itemsUserPerPage]);

  const {
    loading,
    data,
    error: getVendorError,
    refetch,
  } = useQuery(GET_VENDOR_BY_ID, {
    variables: { id: vendorId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const { loading: devicesStatsLoading, data: devicesStatsData } = useQuery(GET_COUNT_OF_REGISTERED_DEVICES, {
    variables: { dataContractName: vendorData?.vendorName, fuzzySearch: true },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !vendorData?.vendorName,
  });

  const { loading: messagesStatsLoading, data: messagesStatsData } = useQuery(GET_COUNT_OF_PUBLISHED_MESSAGES, {
    variables: { dataContractName: vendorData?.vendorName, fuzzySearch: true },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !vendorData?.vendorName,
  });

  const { data: errorStatsData, loading: errorStatsLoading } = useQuery(GET_ERROR_MESSAGES_FOR_PROJECT, {
    variables: {
      input: {
        dataContractName: vendorData?.vendorName,
        fuzzySearch: true,
      },
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip: !vendorData?.vendorName,
  });

  const [deleteVendorRepresentative, { loading: deletionLoading }] = useMutation(DELETE_VENDOR_REPRESENTATIVE);

  const isLoaded =
    vendorData?.vendorName && !loading && !errorStatsLoading && !devicesStatsLoading && !messagesStatsLoading;

  const goToMemberDashboard = () => {
    navigate(PrivateRoute.MemberDashboard, { replace: true });
  };

  const projectPopupCancel = () => {
    setProjectPopupOpened(false);
    setIsEditingProject(false);
  };

  const userPopupCancel = () => {
    setUsersPopupOpened(false);
    setIsEditingUser(false);
  };

  const handleUserAdded = () => {
    refetch();
    setUsersPopupOpened(false);
    setIsEditingUser(false);
  };

  const handleProjectAdded = () => {
    refetch();
  };

  const handleEditUser = (currentUser: VendorRepresentative) => {
    setEditableUser(currentUser);
    setIsEditingUser(true);
    setUsersPopupOpened(true);
  };

  const handleEditProject = (project: DataProducerProject) => {
    setEditableProject(project);
    setIsEditingProject(true);
    setProjectPopupOpened(true);
  };

  const handleEditVendor = () => {
    setIsEditingVendor(true);
    setVendorPopupOpened(true);
  };

  const vendorPopupCancel = () => {
    setVendorPopupOpened(false);
    setIsEditingVendor(false);
  };

  const handleVendorEdited = () => {
    refetch();
    setVendorPopupOpened(false);
  };

  const downloadStats = () => {
    const fileName = `${vendorData.vendorName}-export-${new Date().toLocaleDateString()}.json`;
    const json = JSON.stringify(
      {
        DeviceRegistered: devicesStatsData?.getCountOfRegisteredDevices?.count ?? 0,
        Onboarding: vendorStats?.onboardingPeopleCount ?? 0,
        MessagesPublished:
          calcPublishedMessages(messagesStatsData?.getCountOfPublishedMessages?.vendorMessagesCount) ?? 0,
        MessagesFailed: errorStatsData?.getErrorMessagesForProject?.errorCounter ?? 0,
        CertificateExpiring: vendorStats?.expiredCertificatesCount ?? 0,
      },
      null,
      2,
    );
    const blob = new Blob([json], { type: 'application/json' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    setTextContent(`Export data is successfull`);
    setNotificationBgColor(NotificationBgColor.SUCCESS);
    setIsNotificationOpened(true);

    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  const closeConfirmPopup = () => {
    setConfirmPopupOpened(false);
  };

  const isTeamLead = (roles: VendorRoles) => roles?.length && roles.includes(VendorRoles.VENDOR_TL);

  const deleteUser = async (userToDelete: DeleteUserInput, user: VendorRepresentative) => {
    try {
      await deleteVendorRepresentative({
        variables: userToDelete,
      });
      closeConfirmPopup();
      setTextContent(`${user?.firstName} ${user?.lastName} has been successfully deleted`);
      setNotificationBgColor(NotificationBgColor.SUCCESS);
      setIsNotificationOpened(true);
      setDeleteUserData(null);
      refetch();
    } catch {
      setTextContent('There was an issue with your request');
      setNotificationBgColor(NotificationBgColor.ERROR);
      setIsNotificationOpened(true);
    }
  };

  const handleDeleteUser = (user: VendorRepresentative) => {
    const { id, roles, email } = user;
    const isTL = isTeamLead(roles);

    if (isTL) {
      const userProjects = getTLProjects(vendorData?.projects, email);
      const warningMessage = `This user has the role of Team Lead in the ${
        userProjects || 'project'
      }. Please replace this email with the data producer first, then try again`;

      setWarningText(warningMessage);
      setWarningPopupOpened(true);
      return;
    }

    const isValidData = isIdValid(id) && isIdValid(vendorId ?? '');

    if (!isValidData) {
      setTextContent('Invalid user data');
      setNotificationBgColor(NotificationBgColor.WARNING);
      setIsNotificationOpened(true);
      return;
    }

    const userToDelete: DeleteUserInput = {
      vendorId: vendorId ?? '',
      vendorRepresentativeId: id,
    };

    setDeleteUserData({ userToDelete, user });
    setConfirmPopupOpened(true);
  };

  const handleConfirm = () => {
    if (!deleteUserData) {
      return;
    }

    const { userToDelete, user } = deleteUserData;

    deleteUser(userToDelete, user);
  };

  useEffect(() => {
    if (!loading && data) {
      setVendorData(data?.getVendor);
      setRawProjectsList(data?.getVendor?.projects);
      setRawUsersList(data?.getVendor?.vendorRepresentatives ?? []);
    }
  }, [data]);

  useEffect(() => {
    if (vendorData?.projects) {
      const expiredProjectsCount =
        vendorData.projects?.filter(
          (project) =>
            project?.certificate && isProjectExpired(parseInt(project?.certificate?.expirationTime?.notAfter ?? '0')),
        )?.length ?? 0;
      const onboardingProjectsCount =
        vendorData.projects?.filter((project) => isProjectOnboarding(project?.status))?.length ?? 0;

      setVendorStats({
        onboardingPeopleCount: onboardingProjectsCount,
        expiredCertificatesCount: expiredProjectsCount,
      });
    }
  }, [vendorData]);

  useEffect(() => {
    setPagination((prevValue) => ({
      ...prevValue,
      currPage: 0,
    }));
  }, [pagination.itemsPerPage]);

  useEffect(() => {
    setPagination((prevValue) => ({
      ...prevValue,
      currUserPage: 0,
    }));
  }, [pagination.itemsUserPerPage]);

  useHandleError(getVendorError);

  return (
    <BaseLayout>
      <Stack direction="column" justifyContent="flex-start" className={styles.container}>
        {isLoaded ? (
          <>
            <Stack id="header" direction="column" style={{ marginLeft: '20px' }} spacing={2}>
              <Stack data-testid="breadcrumb" direction="row" justifyContent="flex-start" spacing={1}>
                <ButtonBase onClick={goToMemberDashboard}>
                  <Typography variant={Variant.Body1} color="#63676B">
                    Vendor Manager
                  </Typography>
                </ButtonBase>
                <Typography variant="body1" color="#D5D8D9">
                  /
                </Typography>
                <Typography variant="body1" color="#060F16">
                  Vendor Details
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" justifyContent="flex-start" spacing={2}>
                  <VendorLogo />
                  <Stack data-testid="details" direction="column" alignItems="flex-start" justifyContent="flex-start">
                    <Typography data-testid="vendor-title" variant="h2" fontWeight={600} fontSize={34}>
                      {vendorData?.vendorName || ''} Connection Dashboard
                    </Typography>
                    <Typography data-testid="contact-info" variant="body3">
                      {getVendorAddress(vendorData)}
                    </Typography>
                  </Stack>
                </Stack>
                {isAdmin && (
                  <Button
                    data-testid="edit-vendor"
                    className={styles.editVendorButton}
                    variant="outlined"
                    onClick={handleEditVendor}
                  >
                    Edit Vendor Details
                  </Button>
                )}
              </Stack>
              {activeTab === PageTabs.Project.toString() && rawProjectsList?.length > 0 && (
                <Stack id="stats" direction="row" justifyContent="flex-start" spacing={3}>
                  <StatBox
                    value={devicesStatsData?.getCountOfRegisteredDevices?.count ?? 0}
                    title="Devices registered"
                    accumulation="total"
                  />
                  <StatBox
                    value={vendorStats?.onboardingPeopleCount ?? 0}
                    title="Onboarding in progress"
                    accumulation="total"
                  />
                  <StatBox
                    value={
                      calcPublishedMessages(messagesStatsData?.getCountOfPublishedMessages?.vendorMessagesCount) ?? 0
                    }
                    title="Messages published"
                    accumulation="last 24 hours"
                  />
                  <StatBox
                    value={errorStatsData?.getErrorMessagesForProject?.errorCounter ?? 0}
                    title="Publish errors"
                    accumulation="last 24 hours"
                  />
                  <StatBox
                    value={vendorStats?.expiredCertificatesCount ?? 0}
                    title="Certificates expiring"
                    accumulation="next 60 days"
                  />
                  <Button onClick={() => downloadStats()}>Export Stats</Button>
                </Stack>
              )}
            </Stack>
            <Stack id="body" direction="column" style={{ marginTop: '15px' }}>
              <Stack direction="column" style={{ marginLeft: '20px', marginRight: '25px' }}>
                <TabContext value={activeTab}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList
                      indicatorVariant="line"
                      indicatorWidth="content"
                      onChange={(event, value) => setActiveTab(value)}
                    >
                      <Tab
                        data-testid="data-producers-tab"
                        label="Data Producers"
                        value={PageTabs.Project.toString()}
                      />
                      <Tab data-testid="users-tab" label="Users" value={PageTabs.User.toString()} />
                    </TabList>
                  </Box>
                  <TabPanel value={PageTabs.Project.toString()}>
                    {rawProjectsList?.length > 0 ? (
                      <Stack id="projectsTab" direction="column">
                        <Stack
                          id="search"
                          data-testid="data-producers-actions"
                          direction="row"
                          justifyContent="space-between"
                        >
                          <SearchBox
                            multiple={false}
                            onChange={(event) => setProjectsSearch(event.text)}
                            sx={{
                              maxWidth: '450px',
                              width: '100%',
                            }}
                          />
                          {isAdmin && (
                            <Button data-testid="add-data-producer" onClick={() => setProjectPopupOpened(true)}>
                              + Add A Data Producer
                            </Button>
                          )}
                        </Stack>
                        <Stack id="projectsGrid" direction="row">
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell align="left">
                                    <Typography variant="tableHeader2">Device type</Typography>
                                  </TableCell>
                                  <TableCell align="left">
                                    <Typography variant="tableHeader2">Data Producer Name</Typography>
                                  </TableCell>
                                  <TableCell align="left">
                                    <Typography variant="tableHeader2">Device ID</Typography>
                                  </TableCell>
                                  <TableCell align="left">
                                    <Typography variant="tableHeader2">Status</Typography>
                                  </TableCell>
                                  <TableCell align="left" />
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {paginatedList.map((project, index) => (
                                  <TableRow key={`${project.name}-${index}`}>
                                    <TableCell>
                                      <Typography>{project.type}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography>{`${project.name}`}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography>{project.id.toUpperCase()}</Typography>
                                    </TableCell>
                                    <TableCell>{MapStatusToComponent[project.status]}</TableCell>
                                    <TableCell>
                                      {isAdmin && (
                                        <Button
                                          data-testid={`data-producer-edit-${project.id}`}
                                          className={styles.editButton}
                                          startIcon={<EditIcon />}
                                          color="primary"
                                          fullWidth={false}
                                          onClick={() => handleEditProject(project)}
                                          variant="outlined"
                                        >
                                          Edit
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                              <TableFooter>
                                <TableRow>
                                  <TablePagination
                                    data-testid="table-pagination"
                                    count={searchedProjects.length}
                                    onPageChange={(_, page) =>
                                      setPagination((prevValue) => ({ ...prevValue, currPage: page }))
                                    }
                                    onRowsPerPageChange={(event) =>
                                      setPagination((prevValue) => ({
                                        ...prevValue,
                                        itemsPerPage: parseInt(event.target.value, 10),
                                      }))
                                    }
                                    page={pagination.currPage}
                                    rowsPerPage={pagination.itemsPerPage}
                                    rowsPerPageOptions={[1, 5, 10, 25]}
                                    sx={{
                                      width: '1000px',
                                    }}
                                  />
                                </TableRow>
                              </TableFooter>
                            </Table>
                          </TableContainer>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack
                        data-testid="projects-empty"
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                        spacing={3}
                        style={{
                          borderRadius: '4px',
                          backgroundColor: '#F6F8FA',
                          border: 'solid 1px #E0E0E0',
                          height: '226px',
                        }}
                      >
                        <Typography>This vendor currently has no data producers established.</Typography>
                        {isAdmin && (
                          <Button data-testid="empty-add-new-project" onClick={() => setProjectPopupOpened(true)}>
                            + Add A Data Producer
                          </Button>
                        )}
                      </Stack>
                    )}
                  </TabPanel>
                  <TabPanel value={PageTabs.User.toString()}>
                    {rawUsersList.length > 0 ? (
                      <Stack id="usersTab" direction="column">
                        <Stack id="search" direction="row" justifyContent="space-between">
                          <SearchBox
                            multiple={false}
                            onChange={(event) => setUserSearch(event.text)}
                            sx={{
                              maxWidth: '450px',
                              width: '100%',
                            }}
                          />
                          {isAdmin && (
                            <Button data-testid="add-new-user" onClick={() => setUsersPopupOpened(true)}>
                              + Add A User
                            </Button>
                          )}
                        </Stack>
                        <Stack id="usersGrid" direction="row">
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell align="left">
                                    <Typography variant={Variant.TableHeader2}>First Name</Typography>
                                  </TableCell>
                                  <TableCell align="left">
                                    <Typography variant={Variant.TableHeader2}>Last Name</Typography>
                                  </TableCell>
                                  <TableCell align="left">
                                    <Typography variant={Variant.TableHeader2}>Email Address</Typography>
                                  </TableCell>
                                  <TableCell align="left">
                                    <Typography variant={Variant.TableHeader2}>Read-Only Access</Typography>
                                  </TableCell>
                                  <TableCell align="left"></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {paginatedUserList?.map((user, index) => (
                                  <TableRow key={`${user.firstName}-${index}`}>
                                    <TableCell>
                                      <Typography>{user.firstName}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography>{user.lastName}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography>{user.email}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography>
                                        {user?.roles?.includes(VendorRoles.VENDOR_VIEWER) ? 'Yes' : 'No'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {isAdmin && (
                                        <>
                                          <Button
                                            data-testid={`user-edit-${user.id}`}
                                            className={styles.editButton}
                                            startIcon={<EditIcon />}
                                            color="primary"
                                            fullWidth={false}
                                            onClick={() => handleEditUser(user)}
                                            variant="outlined"
                                          >
                                            Edit
                                          </Button>
                                          <Button
                                            data-testid={`user-delete-${user.id}`}
                                            className={styles.editButton}
                                            startIcon={<DeleteIcon />}
                                            color="primary"
                                            fullWidth={false}
                                            onClick={() => handleDeleteUser(user)}
                                            variant="outlined"
                                          >
                                            Delete
                                          </Button>
                                        </>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                              <TableFooter>
                                <TableRow>
                                  <TablePagination
                                    data-testid="table-pagination"
                                    count={searchedUsers.length}
                                    onPageChange={(_, page) => {
                                      setPagination((prevValue) => ({ ...prevValue, currUserPage: page }));
                                    }}
                                    onRowsPerPageChange={(event) =>
                                      setPagination((prevValue) => ({
                                        ...prevValue,
                                        itemsUserPerPage: parseInt(event.target.value, 10),
                                      }))
                                    }
                                    page={pagination.currUserPage}
                                    rowsPerPage={pagination.itemsUserPerPage}
                                    rowsPerPageOptions={[1, 5, 10, 25]}
                                    sx={{
                                      width: '1000px',
                                    }}
                                  />
                                </TableRow>
                              </TableFooter>
                            </Table>
                          </TableContainer>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                        spacing={3}
                        style={{
                          borderRadius: '4px',
                          backgroundColor: '#F6F8FA',
                          border: 'solid 1px #E0E0E0',
                          height: '226px',
                        }}
                      >
                        <Typography>This vendor currently has no users.</Typography>
                        {isAdmin && <Button onClick={() => setUsersPopupOpened(true)}>+ Invite New User</Button>}
                      </Stack>
                    )}
                  </TabPanel>
                </TabContext>
              </Stack>
            </Stack>
          </>
        ) : (
          <Loader />
        )}
      </Stack>
      <ProjectPopup
        isEditing={isEditingProject}
        vendorData={vendorData}
        handleProjectAdded={handleProjectAdded}
        onCancel={projectPopupCancel}
        opened={projectPopupOpened}
        editableProject={editableProject}
      />
      <UserPopup
        isEditing={isEditingUser}
        editableUser={editableUser}
        onUserAdded={handleUserAdded}
        onCancel={userPopupCancel}
        opened={usersPopupOpened}
        vendorId={vendorId ?? ''}
      />
      <VendorPopup
        onVendorAdded={handleVendorEdited}
        onCancel={vendorPopupCancel}
        opened={vendorPopupOpened}
        isEditing={isEditingVendor}
        editableVendor={vendorData}
      />
      <ConfirmPopup
        disableConfirmButton={deletionLoading}
        confirmTitle="Are you sure you want to delete this user?"
        onCancel={closeConfirmPopup}
        onConfirm={handleConfirm}
        opened={confirmPopupOpened}
      />
      <ConfirmPopup
        confirmTitle="Unable to delete user"
        textContent={warningText}
        confirmButtonText="Ok"
        showCancelButton={false}
        onCancel={() => setWarningPopupOpened(false)}
        onConfirm={() => setWarningPopupOpened(false)}
        opened={warningPopupOpened}
      />
    </BaseLayout>
  );
};
